/**
 * Normalize purchased items from a claim API row (list or detail).
 * @returns {{ name: string, amount: number|null }[]}
 */
export function parseClaimLineItems(claim) {
  if (Array.isArray(claim?.line_items) && claim.line_items.length > 0) {
    return claim.line_items
      .map((row) => ({
        name: String(row?.name ?? row?.item ?? '').trim(),
        amount: parseClaimLineItemAmount(row?.amount),
      }))
      .filter((row) => row.name);
  }

  const fallback = String(claim?.item_bought ?? claim?.claim_title ?? '').trim();
  if (!fallback) return [];

  if (!fallback.includes(',')) {
    return [{ name: fallback, amount: null }];
  }

  return fallback
    .split(',')
    .map((part) => ({ name: part.trim(), amount: null }))
    .filter((row) => row.name);
}

/** Normalize editable form rows to { name, amount }[] */
export function normalizeFormLineItems(items) {
  return (items || [])
    .map((row) => ({
      name: String(row?.name || '').trim(),
      amount: row?.amount === '' || row?.amount == null
        ? null
        : parseFloat(String(row.amount).replace(/[^0-9.]/g, '')),
    }))
    .filter((row) => row.name);
}

function parseClaimLineItemAmount(value) {
  if (value == null || value === '') return null;
  const n = parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return Number.isNaN(n) ? null : n;
}

export function claimLineItemsPrimaryLabel(claim) {
  const items = parseClaimLineItems(claim);
  return items[0]?.name ?? '';
}

export function claimLineItemsSummaryLabel(claim) {
  const items = parseClaimLineItems(claim);
  return buildClaimTitleFromLineItems(items);
}

/**
 * Short summary for lists, API claim_title, and confirm dialogs.
 * Keeps within maxLen (default 255) even with many line items.
 */
export function buildClaimTitleFromLineItems(items, fallback = '', maxLen = 255) {
  const names = (items || [])
    .map((i) => String(i?.name ?? i?.item ?? '').trim())
    .filter(Boolean);

  if (!names.length) {
    return String(fallback || '').trim().slice(0, maxLen);
  }

  if (names.length === 1) {
    return names[0].slice(0, maxLen);
  }

  const maxNames = 3;
  let title = names.slice(0, maxNames).join(', ');
  const extra = names.length - maxNames;
  if (extra > 0) {
    title += ` (+${extra} more)`;
  }

  if (title.length <= maxLen) {
    return title;
  }

  const suffix = ` (+${names.length - 1} more)`;
  const allowed = Math.max(maxLen - suffix.length, 24);
  return `${names[0].slice(0, allowed)}${suffix}`;
}

export function sumClaimLineItemAmounts(items) {
  let total = 0;
  let hasAny = false;
  (items || []).forEach((row) => {
    const n = parseClaimLineItemAmount(row?.amount);
    if (n != null && n > 0) {
      total += n;
      hasAny = true;
    }
  });
  return hasAny ? total : null;
}

export function formatClaimLineItemAmount(amount) {
  if (amount == null) return null;
  return `RM ${amount.toFixed(2)}`;
}

/** @deprecated Use buildClaimTitleFromLineItems */
export function lineItemsSummaryTitle(items) {
  return buildClaimTitleFromLineItems(items);
}
